import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import tripArrow from "../../../assets/images/trip_arrow.png";
import "../../ListOfTripRequest/css/TripCard.css";
import { formatAmount } from "../../../utils/Utils";
import ROUTE_PATHS from "../../../utils/routePaths";
import {
  formatDateTime,
  formatToCustomDate,
  formatDateTimeForUI,
  getTimezoneString,
} from "../../../utils/dateUtils";
import { renderAirportCode } from "../../../utils/Utils";
import useLandingAuthApi from "../../../services/api/useLandingAuthApi";

const UnregisteredAircraftTripCard = ({ tripData, listPage, detailsPage }) => {
  const { getAirportById } = useLandingAuthApi();
  const navigate = useNavigate();

  // Fetch departure airport details
  const { data: departureAirportData, isLoading: isLoadingDeparture } =
    useQuery({
      queryKey: ["airport", tripData?.departureAirportId],
      queryFn: () => getAirportById(tripData?.departureAirportId),
      enabled: !!tripData?.departureAirportId,
      retry: false,
      refetchOnMount: "always",
    });

  // Fetch arrival airport details
  const { data: arrivalAirportData, isLoading: isLoadingArrival } = useQuery({
    queryKey: ["airport", tripData?.arrivalAirportId],
    queryFn: () => getAirportById(tripData?.arrivalAirportId),
    enabled: !!tripData?.arrivalAirportId,
    retry: false,
    refetchOnMount: "always",
  });

  // Check if data is directly in response or nested in data property
  const departureAirport = departureAirportData?.data || departureAirportData;
  const arrivalAirport = arrivalAirportData?.data || arrivalAirportData;
  const departure = formatDateTime(tripData?.departureTime);
  const arrival = formatDateTime(tripData?.arrivalTime);

  // Default to Main Terminal since terminal info is not in the response
  const departureTerminal = "Main Terminal";
  const arrivalTerminal = "Main Terminal";


  // Only show airport code when data is loaded
  const getAirportCodeDisplay = (airport) => {
    if (!airport) return null;
    return renderAirportCode(airport.iataCode, airport.icaoCode);
  };

  const departureCode = getAirportCodeDisplay(departureAirport);
  const arrivalCode = getAirportCodeDisplay(arrivalAirport);

  const handleCardClick = () => {
    // Persist selected trip details so details page doesn't rely on useLocation
    const payload = {
      tripData,
      departureAirport,
      arrivalAirport,
      listPage,
      detailsPage,
    };
    try {
      localStorage.setItem(
        "unregisteredAircraftSelectedTrip",
        JSON.stringify(payload)
      );
    } catch (e) {
      // If storage fails, still navigate; details page will handle missing data
    }

    navigate(ROUTE_PATHS.UNREGISTERED_AIRCRAFT_TRIP_DETAILS, {
      state: { from: "unregistered-aircraft-trips-details" },
    });
  };

  return (
    <div
      className="trip-request-card"
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleCardClick();
      }}
    >
      <div className="trip-header">
        {tripData?.createdAt && (
          <div className="trip-creation-date">
            Creation Date:
            <span>{formatDateTimeForUI(tripData.createdAt)}</span>
          </div>
        )}
      </div>

      {(departureCode || arrivalCode) && (
        <div className="trip-details">
          {departureCode && (
            <p className="trip-request-left">{departureCode}</p>
          )}
          <img src={tripArrow} className="trip-request-arrow-logo" alt="logo" />
          {arrivalCode && <p className="trip-request-right">{arrivalCode}</p>}
        </div>
      )}

      <div className="trip-request-flight-info">
        <div className="trip-request-departure-info">
          {departure?.time && (
            <p>
              <strong>Departure</strong>
              <span>
                {departure.time} ({getTimezoneString()})
              </span>
            </p>
          )}
          {departure?.date && (
            <p>
              <strong>Date</strong>
              <span>{formatToCustomDate(departure.date)}</span>
            </p>
          )}
          {departureTerminal && (
            <p>
              <strong>Terminal</strong>
              <span>{departureTerminal}</span>
            </p>
          )}
        </div>
        <div className="trip-request-arrival-info">
          {arrival?.time && (
            <p>
              <strong>Arrival</strong>
              <span>
                {arrival.time} ({getTimezoneString()})
              </span>
            </p>
          )}
          {arrival?.date && (
            <p>
              <strong>Date</strong>
              <span>{formatToCustomDate(arrival.date)}</span>
            </p>
          )}
          {arrivalTerminal && (
            <p>
              <strong>Terminal</strong>
              <span>{arrivalTerminal}</span>
            </p>
          )}
        </div>
      </div>

      {tripData?.aircraftRegistrationNumber && (
        <div className="trip-request-passenger-info">
          <p>
            <strong>Registration Number</strong>
            <span>{tripData.aircraftRegistrationNumber}</span>
          </p>
          <p>
            <strong>Landing Fee</strong>
            <span>{`BSD ${formatAmount(tripData.landingFee)}`}</span>
          </p>
        </div>
      )}

    </div>
  );
};

export default UnregisteredAircraftTripCard;
